import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const DISPATCH_DAYS = {
  mon_fri: [1, 2, 3, 4, 5],
  mon_sat: [1, 2, 3, 4, 5, 6],
  all: [0, 1, 2, 3, 4, 5, 6],
};

/**
 * Builds the dispatch sentence ("Expediere mâine") from the block's shipping settings.
 * The clock is shifted into store time and read back with the UTC getters, so a visitor
 * in any timezone — and a page served from cache — gets the store's answer, not their own.
 * @param {Record<string, any>} config - Parsed `data-shipping` payload.
 * @param {number} [nowMs] - Current UTC epoch, injectable for tests.
 * @returns {string | null} The sentence, or null when it can't be computed.
 */
export function shippingMessage(config, nowMs = Date.now()) {
  const cutoff = /^(\d{1,2}):(\d{2})$/.exec(String(config.cutoff ?? '').trim());
  const zone = /^([+-])(\d{2})(\d{2})$/.exec(String(config.tz ?? '').trim());
  if (!cutoff || !zone) return null;

  const offset = (zone[1] === '-' ? -1 : 1) * (Number(zone[2]) * 60 + Number(zone[3]));
  const now = new Date(nowMs + offset * 60000);
  const minutesLeft = Number(cutoff[1]) * 60 + Number(cutoff[2]) - (now.getUTCHours() * 60 + now.getUTCMinutes());

  const openDays = DISPATCH_DAYS[config.days] ?? DISPATCH_DAYS.mon_fri;
  const holidays = new Set(config.holidays ?? []);
  const dispatch = new Date(now);
  dispatch.setUTCHours(0, 0, 0, 0);
  const today = dispatch.getTime();
  if (minutesLeft <= 0) dispatch.setUTCDate(dispatch.getUTCDate() + 1);

  let handlingDays = Math.max(0, Number(config.leadDays) || 0);
  let found = false;
  for (let i = 0; i < 60 && !found; i++) {
    if (openDays.includes(dispatch.getUTCDay()) && !holidays.has(dispatch.toISOString().slice(0, 10))) {
      if (handlingDays === 0) found = true;
      else handlingDays--;
    }
    if (!found) dispatch.setUTCDate(dispatch.getUTCDate() + 1);
  }
  // Every day in the next two months closed — nothing honest left to say.
  if (!found) return null;

  const dayDiff = Math.round((dispatch.getTime() - today) / 86400000);
  if (dayDiff === 0) return fillTemplate(config.today, { cutoff: cutoff[0], time: formatDuration(minutesLeft) });
  if (dayDiff === 1) return fillTemplate(config.tomorrow, {});
  return fillTemplate(config.later, { day: formatDay(dispatch, dayDiff, config.locale) });
}

/**
 * Placeholders are `[bracketed]`, not Liquid-style: the theme editor reads `{{ }}` in a
 * block setting as a dynamic source and refuses to save the field.
 * @param {string} template - Merchant copy with `[placeholder]` slots.
 * @param {Record<string, string>} values - Replacements, by placeholder name.
 * @returns {string | null} The filled copy, or null if the merchant cleared the setting.
 */
function fillTemplate(template, values) {
  const text = String(template ?? '').trim();
  if (!text) return null;
  return text.replace(/\[(\w+)\]/g, (match, key) => values[key] ?? match);
}

/**
 * @param {number} minutes - Minutes left before the cut-off.
 * @returns {string} e.g. "2 h 15 min".
 */
function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/**
 * @param {Date} date - Dispatch day, held in UTC fields.
 * @param {number} dayDiff - Days from today; a week out or more also gets the date.
 * @param {string} [locale] - Storefront locale.
 * @returns {string} e.g. "luni" or "luni, 12 august".
 */
function formatDay(date, dayDiff, locale) {
  const options = dayDiff < 7 ? { weekday: 'long' } : { weekday: 'long', day: 'numeric', month: 'long' };
  return new Intl.DateTimeFormat(locale || undefined, { ...options, timeZone: 'UTC' }).format(date);
}

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#renderShipping();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
        this.#renderShipping();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };

  /** Fills the placeholder the block leaves behind, if the settings add up to a message. */
  #renderShipping() {
    const target = this.querySelector('.product-inventory__shipping');
    if (!target || !this.dataset.shipping) return;

    let message = null;
    try {
      message = shippingMessage(JSON.parse(this.dataset.shipping));
    } catch (error) {
      console.warn('[product-inventory] Invalid shipping config:', error);
    }
    if (!message) return;

    target.textContent = message;
    target.hidden = false;
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
